/**
 * Step3CampaignsCard - Campaign Playbook
 * Step 3: Generate a comprehensive playbook of campaign plays
 * 
 * Visual redesign: Cleaner, calmer UI with reduced color noise
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { 
  Megaphone, Check, Loader2, ChevronDown, ChevronUp, Target, Sparkles, 
  AlertTriangle, Building2, DollarSign, Clock, Shield, Plus, Zap, TrendingUp,
  Search, Filter, Star, Layers, Users, BarChart3, RefreshCw, Eye, X, ClipboardCheck,
  ExternalLink, CircleDot, Copy
} from 'lucide-react';
import { plansApi } from '../api/plansApi';
import { bucketWorkflowApi } from '../../bucket-workflow/api';
import CampaignExplainabilityCard from './CampaignExplainabilityCard';

// Constants
const COMPANY_STAGES = [
  { value: 'startup', label: 'Startup', desc: 'Early stage, limited budget' },
  { value: 'seed', label: 'Seed', desc: 'Seed funding, testing channels' },
  { value: 'series_a', label: 'Series A', desc: 'Scaling, multiple channels' },
  { value: 'series_b', label: 'Series B', desc: 'Growth mode, full funnel' },
  { value: 'growth', label: 'Growth', desc: 'Established, efficiency focus' },
  { value: 'post_ipo', label: 'Post-IPO', desc: 'Enterprise, full coverage' }
];

const CHANNELS = [
  { value: 'search_paid', label: 'Search', icon: '🔍' },
  { value: 'meta_paid', label: 'Meta', icon: '📱' },
  { value: 'linkedin_ads', label: 'LinkedIn', icon: '💼' },
  { value: 'retargeting', label: 'Retargeting', icon: '🔄' },
  { value: 'email_lifecycle', label: 'Email', icon: '📧' },
  { value: 'partnerships', label: 'Partners', icon: '🤝' }
];

const STAGES = [
  { value: 'decision', label: 'Decision', dotColor: 'bg-emerald-500' },
  { value: 'consideration', label: 'Consideration', dotColor: 'bg-blue-500' },
  { value: 'retargeting', label: 'Retargeting', dotColor: 'bg-orange-500' },
  { value: 'awareness', label: 'Awareness', dotColor: 'bg-violet-500' },
  { value: 'retention', label: 'Retention', dotColor: 'bg-teal-500' }
];

const TAGS = [
  { value: 'core', label: 'Core', dotColor: 'bg-emerald-500' },
  { value: 'adjacent', label: 'Adjacent', dotColor: 'bg-amber-500' },
  { value: 'edge', label: 'Edge', dotColor: 'bg-slate-400' }
];

const BUDGETS = [
  { value: 'lean', label: 'Lean', desc: 'Focus on high-ROI plays' },
  { value: 'normal', label: 'Normal', desc: 'Balanced approach' },
  { value: 'aggressive', label: 'Aggressive', desc: 'Full funnel coverage' }
];

const TIME_HORIZONS = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' }
];

const BRAND_STANCES = [
  { value: 'safe', label: 'Safe', desc: 'Conservative messaging' },
  { value: 'bold', label: 'Bold', desc: 'Bolder claims and angles' }
];

// Intent Buckets for grouping campaigns - simplified styling
const INTENT_BUCKETS = [
  { 
    value: 'high_intent', 
    label: 'High Intent', 
    stages: ['decision', 'retargeting'],
    subtitle: 'Decision & Retargeting stage campaigns'
  },
  { 
    value: 'mid_intent', 
    label: 'Mid Intent', 
    stages: ['consideration'],
    subtitle: 'Consideration stage campaigns'
  },
  { 
    value: 'low_intent', 
    label: 'Low Intent', 
    stages: ['awareness'],
    subtitle: 'Awareness stage campaigns'
  },
  { 
    value: 'retention', 
    label: 'Retention', 
    stages: ['retention'],
    subtitle: 'Retention stage campaigns'
  }
];

function getIntentBucket(stage) {
  for (const bucket of INTENT_BUCKETS) {
    if (bucket.stages.includes(stage)) {
      return bucket;
    }
  }
  return INTENT_BUCKETS[2];
}

// Helper functions
function getScoreClass(score) {
  if (score >= 80) return 'text-slate-700 font-semibold';
  if (score >= 65) return 'text-slate-600';
  return 'text-slate-500';
}

function getStageConfig(stage) {
  return STAGES.find(s => s.value === stage) || STAGES[0];
}

function getChannelInfo(channel) {
  return CHANNELS.find(c => c.value === channel) || { label: channel, icon: '📢' };
}

function getTagConfig(tag) {
  return TAGS.find(t => t.value === tag) || TAGS[2];
}

// Landing Page Context Section Component with copy-to-clipboard
function LandingPageContextSection({ context }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(context);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };
  
  return (
    <div className="border-t border-slate-100 pt-2.5 mt-1">
      <div className="flex items-center gap-2 text-xs font-medium text-teal-700 uppercase tracking-wide mb-2">
        <Layers className="w-3.5 h-3.5" />
        Landing Page Context
      </div>
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 relative group">
        <p className="text-sm text-teal-800 pr-8 leading-relaxed">{context}</p>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-1.5 rounded-md bg-teal-100 hover:bg-teal-200 text-teal-600 transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-4 h-4 text-teal-700" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
        {copied && (
          <span className="absolute top-2 right-10 text-xs text-teal-600 font-medium">Copied!</span>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-1.5 italic">
        Use this context for downstream landing page personalization
      </p>
    </div>
  );
}

// Play Card Component - Cleaner, more scannable design
function PlayCard({ play, isSelected, isExpanded, onToggleSelect, onToggleExpand, segmentName }) {
  const score = play.recommendation_score || 0;
  const stage = getStageConfig(play.stage);
  const channel = getChannelInfo(play.channel);
  const tag = getTagConfig(play.tag);
  const messaging = play.messaging_angle || {};
  const landing = play.landing || {};
  const targeting = play.targeting_unit || {};
  const evidence = play.evidence || [];
  const dependencies = play.dependencies || [];
  const hooks = messaging.example_hooks || [];
  const isTopPick = score >= 75;
  const validationChecks = play.validation?.checks || [];
  
  return (
    <div 
      className={`border rounded-xl transition-all duration-200 bg-white ${
        isSelected 
          ? 'border-l-4 border-l-purple-500 border-t-slate-200 border-r-slate-200 border-b-slate-200 bg-purple-50/30' 
          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
      }`}
      data-testid={`play-card-${play.play_id}`}
    >
      {/* Header - Compact and scannable */}
      <div className="p-4 cursor-pointer" onClick={onToggleExpand}>
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <div 
            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 cursor-pointer transition-all ${
              isSelected 
                ? 'bg-purple-600 border-purple-600' 
                : 'border-slate-300 hover:border-purple-400'
            }`}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          >
            {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title row */}
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-sm text-slate-800 line-clamp-1">{play.name}</h4>
              {isTopPick && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  Top Pick
                </span>
              )}
            </div>
            
            {/* Segment name */}
            {segmentName && (
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                {segmentName}
              </p>
            )}
            
            {/* Pills row - Compact neutral pills */}
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {/* Stage pill with colored dot */}
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                <span className={`w-1.5 h-1.5 rounded-full ${stage.dotColor}`}></span>
                {stage.label}
              </span>
              
              {/* Channel pill */}
              <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                {channel.label}
              </span>
              
              {/* Tag pill with dot */}
              <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200">
                <span className={`w-1.5 h-1.5 rounded-full ${tag.dotColor}`}></span>
                {tag.label}
              </span>
              
              {/* Landing pill */}
              {landing.type && (
                <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                  {landing.type === 'personalize_existing' ? 'Personalize' : 'New Page'}
                </span>
              )}
              
              {/* Validation hint */}
              {validationChecks.length > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200">
                  <ClipboardCheck className="w-3 h-3" />
                  {validationChecks.length} checks
                </span>
              )}
              
              {/* LP Context Ready pill */}
              {play.landing_page_context && (
                <span className="inline-flex items-center gap-1 text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                  <Layers className="w-3 h-3" />
                  LP Context
                </span>
              )}
            </div>
            
            {/* Description - truncated */}
            <p className="text-xs text-slate-500 mt-2 line-clamp-1">{play.why_recommended}</p>
          </div>
          
          {/* Right side: Score + Expand */}
          <div className="shrink-0 flex items-center gap-3">
            <span className={`text-base ${getScoreClass(score)}`}>{score}</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Details - Clean accordion panels, all open by default */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-3">
          {/* Messaging Angle */}
          <details className="group" open>
            <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 uppercase tracking-wide py-1.5 hover:text-slate-800">
              <Megaphone className="w-3.5 h-3.5" />
              Messaging Angle
              <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-2 pl-5">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-sm font-medium text-slate-700">{messaging.angle_name}</span>
                {messaging.cta && (
                  <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                    CTA: {messaging.cta}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 mb-2">{messaging.promise}</p>
              {hooks.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {hooks.map((hook, i) => (
                    <span key={i} className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                      "{hook}"
                    </span>
                  ))}
                </div>
              )}
            </div>
          </details>
          
          {/* Targeting Unit */}
          <details className="group" open>
            <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 uppercase tracking-wide py-1.5 hover:text-slate-800 border-t border-slate-100 pt-2.5">
              <Target className="w-3.5 h-3.5" />
              Targeting
              <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-2 pl-5">
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                {targeting.type?.replace(/_/g, ' ')}
              </span>
              {targeting.keyword_themes && (
                <p className="text-xs text-slate-600 mt-2">
                  <span className="text-slate-500">Keywords:</span> {targeting.keyword_themes?.join(', ')}
                </p>
              )}
              {targeting.interest_themes && (
                <p className="text-xs text-slate-600 mt-1">
                  <span className="text-slate-500">Interests:</span> {targeting.interest_themes?.join(', ')}
                </p>
              )}
              {targeting.trigger_events && (
                <p className="text-xs text-slate-600 mt-1">
                  <span className="text-slate-500">Triggers:</span> {targeting.trigger_events?.join(', ')}
                </p>
              )}
              {targeting.job_title_themes && (
                <p className="text-xs text-slate-600 mt-1">
                  <span className="text-slate-500">Job Titles:</span> {targeting.job_title_themes?.join(', ')}
                </p>
              )}
            </div>
          </details>
          
          {/* Landing */}
          {landing.type && (
            <details className="group" open>
              <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 uppercase tracking-wide py-1.5 hover:text-slate-800 border-t border-slate-100 pt-2.5">
                <Layers className="w-3.5 h-3.5" />
                Landing
                <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-2 pl-5">
                {landing.type === 'personalize_existing' ? (
                  <>
                    <p className="text-xs text-slate-500 mb-1.5">Personalize sections:</p>
                    <div className="flex flex-wrap gap-1">
                      {(landing.personalize_sections || []).map((s, i) => (
                        <span key={i} className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                    {landing.existing_page_refs?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-2">
                        Pages: {landing.existing_page_refs.join(', ')}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mb-1.5">New page outline:</p>
                    <div className="space-y-1">
                      {(landing.new_page_outline || []).map((item, i) => (
                        <p key={i} className="text-xs text-slate-600">
                          <span className="font-medium">{item.section}:</span> {item.notes}
                        </p>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </details>
          )}
          
          {/* Landing Page Context - for LP Generator */}
          {play.landing_page_context && (
            <LandingPageContextSection context={play.landing_page_context} />
          )}
          
          {/* Evidence & Dependencies Row */}
          {(evidence.length > 0 || dependencies.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-slate-100 pt-3">
              {evidence.length > 0 && (
                <details className="group" open>
                  <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 uppercase tracking-wide py-1 hover:text-slate-800">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Evidence ({evidence.length})
                    <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="mt-2 space-y-1.5">
                    {evidence.map((ev, i) => (
                      <div key={i} className="text-xs flex gap-2 items-start">
                        <span className="text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">{ev.source}</span>
                        <span className="text-slate-600">{ev.detail}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              
              {dependencies.length > 0 && (
                <details className="group" open>
                  <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 uppercase tracking-wide py-1 hover:text-slate-800">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Dependencies ({dependencies.length})
                    <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="mt-2 space-y-1.5">
                    {dependencies.map((dep, i) => (
                      <div key={i} className="text-xs flex gap-2 items-start">
                        <span className={`px-1.5 py-0.5 rounded shrink-0 ${
                          dep.severity === 'high' ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50'
                        }`}>
                          {dep.severity}
                        </span>
                        <span className="text-slate-600">{dep.detail}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          
          {/* VALIDATE Section */}
          {validationChecks.length > 0 && (
            <details className="group" open>
              <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 uppercase tracking-wide py-1.5 hover:text-slate-800 border-t border-slate-100 pt-2.5">
                <ClipboardCheck className="w-3.5 h-3.5" />
                Validate ({validationChecks.length} checks)
                <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-2 pl-5 space-y-2">
                {validationChecks.map((check, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-medium text-slate-700">{i + 1}. {check.name}</span>
                      {check.owner && (
                        <span className="text-xs text-slate-500">{check.owner}</span>
                      )}
                    </div>
                    <div className="space-y-1 text-xs">
                      <p className="text-slate-600"><span className="text-slate-500">Action:</span> {check.action}</p>
                      <p className="text-slate-600"><span className="text-emerald-600">Pass if:</span> {check.pass_threshold}</p>
                      <p className="text-slate-600"><span className="text-amber-600">If failed:</span> {check.if_failed}</p>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// Playbook Overview Component - Neutral cards, only Selected gets accent
function PlaybookOverview({ plays, selectedCount }) {
  const totalPlays = plays.length;
  const avgScore = totalPlays > 0 
    ? Math.round(plays.reduce((a, p) => a + (p.recommendation_score || 0), 0) / totalPlays) 
    : 0;
  const topPicks = plays.filter(p => p.recommendation_score >= 80).length;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {/* Total Plays */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-slate-500 mb-1">
          <Layers className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">Total Plays</span>
        </div>
        <p className="text-2xl font-semibold text-slate-800">{totalPlays}</p>
      </div>
      
      {/* Top Picks */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-slate-500 mb-1">
          <Star className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">Top Picks</span>
        </div>
        <p className="text-2xl font-semibold text-slate-800">{topPicks}</p>
      </div>
      
      {/* Avg Score */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-slate-500 mb-1">
          <TrendingUp className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">Avg Score</span>
        </div>
        <p className="text-2xl font-semibold text-slate-800">{avgScore}</p>
      </div>
      
      {/* Selected - Highlighted with purple */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-purple-600 mb-1">
          <Check className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">Selected</span>
        </div>
        <p className="text-2xl font-semibold text-purple-700">{selectedCount}</p>
      </div>
    </div>
  );
}

// Filter Bar Component - Clean single row
function FilterBar({ filters, setFilters, plays }) {
  const stages = [...new Set(plays.map(p => p.stage))];
  const channels = [...new Set(plays.map(p => p.channel))];
  
  const clearFilters = () => setFilters({ stage: null, channel: null, tag: null, search: '' });
  const hasFilters = filters.stage || filters.channel || filters.tag || filters.search;
  
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
      {/* Search - Full width on mobile */}
      <div className="relative flex-1 min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search plays..."
          value={filters.search || ''}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-slate-50"
          data-testid="filter-search"
        />
      </div>
      
      {/* Filters Row - Stack on mobile */}
      <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
        {/* Stage Filter */}
        <Select value={filters.stage || 'all'} onValueChange={v => setFilters(f => ({ ...f, stage: v === 'all' ? null : v }))}>
          <SelectTrigger className="w-full md:w-[130px] h-9 text-sm border-slate-200" data-testid="filter-stage">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {stages.map(s => (
              <SelectItem key={s} value={s}>{getStageConfig(s).label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Channel Filter */}
        <Select value={filters.channel || 'all'} onValueChange={v => setFilters(f => ({ ...f, channel: v === 'all' ? null : v }))}>
          <SelectTrigger className="w-full md:w-[130px] h-9 text-sm border-slate-200" data-testid="filter-channel">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Channels</SelectItem>
            {channels.map(c => (
              <SelectItem key={c} value={c}>{getChannelInfo(c).label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Tag Filter */}
        <Select value={filters.tag || 'all'} onValueChange={v => setFilters(f => ({ ...f, tag: v === 'all' ? null : v }))}>
          <SelectTrigger className="w-full md:w-[110px] h-9 text-sm border-slate-200" data-testid="filter-tag">
            <SelectValue placeholder="Tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {TAGS.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Clear */}
        {hasFilters && (
          <button 
            onClick={clearFilters} 
            className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap px-2 py-1"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

// Main Component
export default function Step3CampaignsCard({ 
  planId, 
  planName,
  assetId,
  assetName,
  stepData, 
  step0Data,
  step1Data,
  step2Data, 
  isCompleted, 
  onSave,
  onBucketConfirmed,
  saving 
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [plays, setPlays] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedPlays, setExpandedPlays] = useState(new Set());
  const [segments, setSegments] = useState([]);
  
  // Settings
  const [companyStage, setCompanyStage] = useState('startup');
  const [channelFocus, setChannelFocus] = useState([]);
  const [budgetLevel, setBudgetLevel] = useState('normal');
  const [timeHorizon, setTimeHorizon] = useState('30d');
  const [brandStance, setBrandStance] = useState('safe');
  
  const [showSettings, setShowSettings] = useState(true);
  const [filters, setFilters] = useState({ stage: null, channel: null, tag: null, search: '' });
  
  // Extract goal, offer, productFocus from step data
  const goal = step0Data?.selected_goal || step0Data?.primary_goal || '';
  const offer = step0Data?.offer_cta || step0Data?.primary_offer || '';
  const productFocus = step0Data?.plan_focus || step2Data?.plan_focus || '';
  
  // Get selected segments from step2Data
  const step2Segments = step2Data?.generated_segments || [];
  const selectedSegmentIds = step2Data?.selected_segment_ids || [];
  const selectedSegments = selectedSegmentIds.length > 0 
    ? step2Segments.filter(s => selectedSegmentIds.includes(s.segment_id))
    : (step2Data?.selected_segments || segments);

  // Load saved plays
  const loadPlays = useCallback(async () => {
    setLoading(true);
    try {
      const stepCampaigns = stepData?.generated_campaigns || [];
      if (stepCampaigns.length > 0) {
        setPlays(stepCampaigns);
        setShowSettings(false);
        const controls = stepData?.user_controls || {};
        setCompanyStage(controls.company_stage || 'startup');
        setChannelFocus(controls.channel_focus || []);
        setBudgetLevel(controls.budget_level || 'normal');
        setTimeHorizon(controls.time_horizon || '30d');
        setBrandStance(controls.brand_stance || 'safe');
        setSelectedIds(stepData?.selected_campaign_ids || []);
        setLoading(false);
        return;
      }
      
      const data = await plansApi.getCampaigns(planId);
      const saved = data.campaigns || data.plays || [];
      if (saved.length > 0) {
        setPlays(saved);
        setShowSettings(false);
      }
      const controls = data.user_controls || {};
      setCompanyStage(controls.company_stage || 'startup');
      setChannelFocus(controls.channel_focus || []);
      setBudgetLevel(controls.budget_level || 'normal');
      setTimeHorizon(controls.time_horizon || '30d');
      setBrandStance(controls.brand_stance || 'safe');
      setSelectedIds(data.selected_campaign_ids || []);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [planId, stepData]);

  useEffect(() => {
    if (stepData?.selected_campaign_ids) {
      setSelectedIds(stepData.selected_campaign_ids);
    }
  }, [stepData]);

  useEffect(() => {
    loadPlays();
  }, [loadPlays]);

  // Generate playbook
  const generatePlaybook = async () => {
    if (!companyStage) {
      toast.error('Select company stage');
      return;
    }
    setGenerating(true);
    try {
      const controls = {
        company_stage: companyStage,
        channel_focus: channelFocus.length > 0 ? channelFocus : 'auto',
        geo: { mode: 'any' },
        budget_level: budgetLevel,
        time_horizon: timeHorizon,
        brand_stance: brandStance
      };
      const data = await plansApi.generateCampaigns(planId, controls);
      if (data.error) {
        toast.error(data.error);
        return;
      }
      const newPlays = data.campaigns || data.plays || [];
      setPlays(newPlays);
      setSegments(data.selected_segments || []);
      setShowSettings(false);
      
      const topPicks = newPlays.filter(p => p.recommendation_score >= 70).map(p => p.play_id || p.campaign_id);
      setSelectedIds(topPicks);
      toast.success(`Generated ${newPlays.length} campaign plays`);
    } catch (err) {
      console.error('Generate error:', err);
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Generate more plays
  const generateMore = async () => {
    setGenerating(true);
    try {
      const existingIds = plays.map(p => p.play_id || p.campaign_id);
      const controls = {
        company_stage: companyStage,
        channel_focus: channelFocus.length > 0 ? channelFocus : 'auto',
        geo: { mode: 'any' },
        budget_level: budgetLevel,
        time_horizon: timeHorizon,
        brand_stance: brandStance
      };
      const data = await plansApi.generateCampaigns(planId, controls, existingIds);
      if (data.error) {
        toast.error(data.error);
        return;
      }
      const allPlays = data.campaigns || data.plays || [];
      setPlays(allPlays);
      toast.success('Added more plays');
    } catch (err) {
      toast.error('Failed to generate more');
    } finally {
      setGenerating(false);
    }
  };

  // Selection handlers
  const toggleSelection = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleExpand = (id) => {
    setExpandedPlays(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const toggleChannel = (ch) => {
    setChannelFocus(prev => prev.includes(ch) ? prev.filter(x => x !== ch) : [...prev, ch]);
  };

  // Filter plays
  const filteredPlays = useMemo(() => {
    return plays.filter(p => {
      if (filters.stage && p.stage !== filters.stage) return false;
      if (filters.channel && p.channel !== filters.channel) return false;
      if (filters.tag && p.tag !== filters.tag) return false;
      if (filters.search) {
        const search = filters.search.toLowerCase();
        const name = (p.name || '').toLowerCase();
        const why = (p.why_recommended || '').toLowerCase();
        if (!name.includes(search) && !why.includes(search)) return false;
      }
      return true;
    });
  }, [plays, filters]);

  // Group by intent bucket
  const playsByIntentBucket = useMemo(() => {
    const groups = {};
    INTENT_BUCKETS.forEach(bucket => {
      groups[bucket.value] = [];
    });
    
    filteredPlays.forEach(p => {
      const bucket = getIntentBucket(p.stage);
      groups[bucket.value].push(p);
    });
    
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => (b.recommendation_score || 0) - (a.recommendation_score || 0));
    });
    
    return groups;
  }, [filteredPlays]);

  const getSegmentName = (sid) => {
    const step2Seg = step2Segments.find(s => s.segment_id === sid);
    if (step2Seg) return step2Seg.name;
    const seg = segments.find(s => s.segment_id === sid);
    return seg ? seg.name : sid;
  };

  // Save handler
  const handleSave = async () => {
    const controls = {
      company_stage: companyStage,
      channel_focus: channelFocus.length > 0 ? channelFocus : 'auto',
      geo: { mode: 'any' },
      budget_level: budgetLevel,
      time_horizon: timeHorizon,
      brand_stance: brandStance
    };
    await onSave({ 
      generated_campaigns: plays, 
      selected_campaign_ids: selectedIds, 
      user_controls: controls 
    });
  };

  // Confirm handler
  const handleConfirm = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one campaign');
      return;
    }
    
    setConfirming(true);
    try {
      await handleSave();
      
      const selectedCampaigns = plays
        .filter(p => selectedIds.includes(p.play_id || p.campaign_id))
        .map(p => ({
          ...p,
          segment_name: getSegmentName(p.segment_id)
        }));
      
      // Build snapshot path for independent execution workspace
      const step0Snapshot = {
        primary_goal: step0Data?.selected_goal || step0Data?.primary_goal || '',
        offer: step0Data?.offer_cta || step0Data?.primary_offer || '',
        target_audience: step0Data?.target_audience || '',
        business_model: step0Data?.business_model || ''
      };
      
      // Get selected personas from step1Data
      const allPersonas = [
        ...(step1Data?.suggested_personas || []),
        ...(step1Data?.plan_personas || [])
      ];
      const selectedPersonaIds = step1Data?.selected_persona_ids || [];
      const selectedPersonas = selectedPersonaIds.length > 0
        ? allPersonas.filter(p => selectedPersonaIds.includes(p.persona_id))
        : allPersonas;
      
      const step1Snapshot = {
        selected_personas: selectedPersonas
      };
      
      const step2Snapshot = {
        selected_segments: selectedSegments,
        focus: productFocus,
        product_interest: step2Data?.product_interest || ''
      };
      
      const step3Snapshot = {
        company_stage: companyStage,
        channels_selected: channelFocus.length > 0 ? channelFocus : 'auto',
        budget: budgetLevel,
        time_horizon: timeHorizon,
        stance: brandStance
      };
      
      const result = await bucketWorkflowApi.createBucket({
        asset_id: assetId || null,
        asset_name: assetName || null,
        plan_id: planId,
        plan_name: planName || 'Unnamed Plan',
        selected_campaigns: selectedCampaigns,
        idempotency_key: `${planId}_${Date.now()}`,
        // Include snapshot path for independent execution workspace
        step0_goal_offer: step0Snapshot,
        step1_personas: step1Snapshot,
        step2_segments: step2Snapshot,
        step3_constraints: step3Snapshot
      });
      
      // Call the callback with confirmed plan info for signals panel
      if (onBucketConfirmed) {
        // Get the confirmed plan ID from the result
        const confirmedPlanId = result.confirmed_plan_id || result.bucket_id || `cp_${Date.now()}`;
        const confirmedCampaignIds = result.confirmed_campaign_ids || 
          selectedCampaigns.map(c => c.play_id || c.campaign_id);
        
        // Build asset bundle for signal generation
        const assetBundle = {
          products_services: step0Data?.products_services || [],
          business_goals: step0Data?.business_goals || [],
          website_analysis: {
            about: step0Data?.about || '',
            target_audience: step0Data?.target_audience || '',
            value_propositions: step0Data?.value_propositions || []
          }
        };
        
        onBucketConfirmed({
          confirmedPlanId,
          confirmedCampaignIds,
          assetBundle
        });
      }
      
      toast.success(
        <div className="flex items-center gap-3">
          <span>{result.message || 'Saved to Bucket List'}</span>
          <button 
            onClick={() => navigate('/workflow')}
            className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 flex items-center gap-1"
          >
            View <ExternalLink className="w-3 h-3" />
          </button>
        </div>,
        { duration: 5000 }
      );
      
    } catch (err) {
      console.error('Confirm error:', err);
      toast.error('Failed to save to Bucket List');
    } finally {
      setConfirming(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="step3-campaigns-card">
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </div>
    );
  }

  const totalPlays = plays.length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="step3-campaigns-card">
      {/* Header - Clean, minimal */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Megaphone className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Campaign Playbook</h3>
            <p className="text-xs text-slate-500">Generate strategic campaign plays for your segments</p>
          </div>
          {totalPlays > 0 && (
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {totalPlays} plays
            </span>
          )}
        </div>
        {totalPlays > 0 && !showSettings && (
          <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} className="text-slate-600">
            <Zap className="w-4 h-4 mr-1" /> Settings
          </Button>
        )}
      </div>

      <div className="p-6 space-y-6">
        {/* Settings Panel - Cleaner design */}
        {showSettings && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-5">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Building2 className="w-4 h-4 text-purple-500" />
              Playbook Settings
            </div>

            {/* Company Stage */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                Company Stage <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {COMPANY_STAGES.map(stage => (
                  <button
                    key={stage.value}
                    onClick={() => setCompanyStage(stage.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      companyStage === stage.value 
                        ? 'border-purple-400 bg-purple-50' 
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`stage-${stage.value}`}
                  >
                    <div className="text-sm font-medium text-slate-700">{stage.label}</div>
                    <div className="text-xs text-slate-500">{stage.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Channel Focus */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                Channel Focus <span className="text-xs text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {CHANNELS.map(ch => (
                  <button
                    key={ch.value}
                    onClick={() => toggleChannel(ch.value)}
                    className={`px-3 py-1.5 rounded-full border text-sm transition-all flex items-center gap-1.5 ${
                      channelFocus.includes(ch.value) 
                        ? 'border-purple-400 bg-purple-100 text-purple-700' 
                        : 'border-slate-200 bg-white hover:border-slate-300 text-slate-600'
                    }`}
                    data-testid={`channel-${ch.value}`}
                  >
                    <span>{ch.icon}</span>
                    <span>{ch.label}</span>
                  </button>
                ))}
              </div>
              {channelFocus.length === 0 && (
                <p className="text-xs text-slate-500">Leave empty to auto-select based on business model</p>
              )}
            </div>

            {/* Budget, Time, Brand */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Budget</label>
                <Select value={budgetLevel} onValueChange={setBudgetLevel}>
                  <SelectTrigger className="border-slate-200" data-testid="budget-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUDGETS.map(b => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Time Horizon</label>
                <Select value={timeHorizon} onValueChange={setTimeHorizon}>
                  <SelectTrigger className="border-slate-200" data-testid="time-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_HORIZONS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Brand Stance</label>
                <Select value={brandStance} onValueChange={setBrandStance}>
                  <SelectTrigger className="border-slate-200" data-testid="brand-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BRAND_STANCES.map(b => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Generate Button */}
            <div className="pt-3 flex gap-3">
              <Button 
                onClick={generatePlaybook} 
                disabled={generating || !companyStage} 
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="generate-playbook-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Generate Playbook
              </Button>
              {totalPlays > 0 && (
                <Button variant="outline" onClick={() => setShowSettings(false)}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Playbook Content */}
        {totalPlays > 0 && !showSettings && (
          <>
            {/* Overview Stats */}
            <PlaybookOverview plays={plays} selectedCount={selectedIds.length} />
            
            {/* Explainability Card */}
            <CampaignExplainabilityCard
              goal={goal}
              offer={offer}
              companyStage={companyStage}
              budget={budgetLevel}
              timeHorizon={timeHorizon}
              stance={brandStance}
              selectedSegments={selectedSegments.length > 0 ? selectedSegments : segments}
              plays={plays}
              productFocus={productFocus}
              channelsSelected={channelFocus}
            />

            {/* Filter Bar */}
            <FilterBar filters={filters} setFilters={setFilters} plays={plays} />

            {/* Plays by Intent Bucket - Clean group headers */}
            <div className="space-y-6">
              {INTENT_BUCKETS.map(bucket => {
                const bucketPlays = playsByIntentBucket[bucket.value] || [];
                if (bucketPlays.length === 0) return null;
                
                return (
                  <div key={bucket.value} className="space-y-3">
                    {/* Intent Bucket Header - Minimal, neutral */}
                    <div className="flex items-center justify-between py-2 border-b border-slate-200">
                      <div>
                        <h3 className="font-semibold text-sm text-slate-800">{bucket.label}</h3>
                        <p className="text-xs text-slate-500">{bucket.subtitle}</p>
                      </div>
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                        {bucketPlays.length} {bucketPlays.length === 1 ? 'play' : 'plays'}
                      </span>
                    </div>
                    
                    {/* Plays in this bucket */}
                    <div className="space-y-2">
                      {bucketPlays.map(play => (
                        <PlayCard
                          key={play.play_id || play.campaign_id}
                          play={play}
                          isSelected={selectedIds.includes(play.play_id || play.campaign_id)}
                          isExpanded={expandedPlays.has(play.play_id || play.campaign_id)}
                          onToggleSelect={() => toggleSelection(play.play_id || play.campaign_id)}
                          onToggleExpand={() => toggleExpand(play.play_id || play.campaign_id)}
                          segmentName={getSegmentName(play.segment_id)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Generate More */}
            {totalPlays < 60 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={generateMore} 
                disabled={generating} 
                className="w-full border-dashed border-slate-300 text-slate-600 hover:text-slate-800 hover:border-slate-400"
                data-testid="generate-more-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Generate More Plays
              </Button>
            )}
          </>
        )}

        {/* Empty State */}
        {totalPlays === 0 && !showSettings && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
              <Megaphone className="w-8 h-8 text-slate-400" />
            </div>
            <h4 className="font-medium text-slate-700 mb-1">No Playbook Yet</h4>
            <p className="text-sm text-slate-500 mb-4">Configure settings and generate your campaign playbook</p>
            <Button variant="outline" onClick={() => setShowSettings(true)}>
              <Zap className="w-4 h-4 mr-2" /> Configure Playbook
            </Button>
          </div>
        )}
      </div>

      {/* Sticky Confirm Bar */}
      {totalPlays > 0 && !showSettings && (
        <div className="sticky bottom-0 p-4 bg-white border-t border-slate-200 flex items-center justify-between">
          <span className="text-sm text-slate-600">
            <span className="font-medium">{selectedIds.length}</span> selected
          </span>
          <div className="flex gap-3">
            <Button 
              onClick={handleConfirm} 
              disabled={confirming || saving || selectedIds.length === 0} 
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-playbook-btn"
            >
              {confirming || saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Add To Bucket List
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
